import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface CloneAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newName: string) => void;
  agentName: string;
  isCloning?: boolean;
}

export function CloneAgentModal({
  isOpen,
  onClose,
  onConfirm,
  agentName,
  isCloning = false,
}: CloneAgentModalProps) {
  const [newName, setNewName] = useState('');

  // Reset name field when modal opens with new agent
  useEffect(() => {
    if (isOpen && agentName) {
      setNewName(`Copy of ${agentName}`);
    }
  }, [isOpen, agentName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim()) {
      onConfirm(newName.trim());
    }
  };

  const handleClose = () => {
    setNewName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative z-50 w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="clone-modal-title"
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 id="clone-modal-title" className="text-lg font-semibold text-gray-900">
              Clone Agent
            </h3>
          </div>

          <p className="mb-4 text-sm text-gray-600">
            Create a copy of{' '}
            <span className="font-semibold text-gray-900">"{agentName}"</span>{' '}
            with all its configuration and settings.
          </p>

          <div className="mb-6 space-y-2">
            <Label htmlFor="clone-name">New Agent Name</Label>
            <Input
              id="clone-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter name for the cloned agent"
              disabled={isCloning}
              autoFocus
              required
              minLength={1}
              maxLength={100}
            />
            <p className="text-xs text-gray-500">
              The cloned agent will be created as inactive.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isCloning}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isCloning || !newName.trim()}
            >
              {isCloning ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner />
                  Cloning...
                </span>
              ) : (
                'Clone Agent'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default CloneAgentModal;
